// ✅ الكود الكامل المعدّل
import { useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Mic,
  Save,
  ArrowRight,
  Loader2,
  UploadCloud,
  X,
  Video,
  Image as ImageIcon,
} from "lucide-react";
import { espQuestionsAPI } from "../../services/espService";

export default function AddSpeakingVideoToEsp() {
  const navigate = useNavigate();
  const { skillId } = useParams();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    title: "",
    description: "",
    duration: 0,
    difficulty: "MEDIUM",
  });
  const [videoFile, setVideoFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVideoFile(file);
    if (!form.title.trim()) {
      setForm((f) => ({ ...f, title: file.name.replace(/\.[^/.]+$/, "") }));
    }
  };

  const handleRemoveFile = () => {
    setVideoFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("يرجى اختيار ملف صورة فقط");
      return;
    }

    setError("");
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError("العنوان مطلوب");
      return;
    }
    if (!videoFile) {
      setError("يرجى رفع ملف الفيديو");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const formData = new FormData();
      formData.append("title", form.title);
      formData.append("video_file", videoFile);
      formData.append("description", form.description);
      formData.append("duration", parseInt(form.duration) || 0);
      formData.append("difficulty", form.difficulty);
      formData.append("esp_skill", parseInt(skillId));
      if (imageFile) {
        formData.append("video_image", imageFile);
      }

      const res = await espQuestionsAPI.createSpeakingVideo(formData);
      navigate(
        `/dashboard/esp/skills/${skillId}/add/speaking/video/${res.video.id}/questions`
      );
    } catch (err) {
      setError(err.response?.data?.error || "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/dashboard/esp/skills/${skillId}`)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowRight className="w-5 h-5 text-gray-600" />
        </button>
        <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
          <Mic className="w-5 h-5 text-orange-700" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">
          إضافة فيديو Speaking
        </h1>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5"
      >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm text-right">
            {error}
          </div>
        )}

        {/* Video Upload */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-right">
            ملف الفيديو <span className="text-red-500">*</span>
          </label>
          {!videoFile ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-gray-200 rounded-xl p-8 flex flex-col items-center gap-3 hover:border-orange-400 hover:bg-orange-50 transition-colors group"
            >
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                <UploadCloud className="w-6 h-6 text-orange-600" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-700">
                  اضغط لرفع ملف الفيديو
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  MP4 · MOV · AVI · WEBM
                </p>
              </div>
            </button>
          ) : (
            <div className="border border-orange-200 bg-orange-50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="flex-1 min-w-0 text-right">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {videoFile.name}
                  </p>
                  <p className="text-xs text-gray-400">
                    {(videoFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <div className="w-9 h-9 bg-orange-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Video className="w-4 h-4 text-orange-700" />
                </div>
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {/* Thumbnail Image (Optional) */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-right">
            صورة مصغرة (اختياري)
          </label>
          {imagePreview ? (
            <div className="relative inline-block">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full max-h-48 object-contain rounded-xl border border-gray-200"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors text-sm font-medium bg-white border-orange-400 text-orange-600 hover:bg-orange-50 w-fit">
              <ImageIcon className="w-4 h-4" />
              رفع صورة مصغرة
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
            </label>
          )}
        </div>

        {/* Title */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-right">
            العنوان <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-right"
          />
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 text-right">
            الوصف
          </label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-right resize-none"
          />
        </div>

        {/* Duration + Difficulty */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700 text-right">
              المدة (بالثواني)
            </label>
            <input
              type="number"
              value={form.duration}
              onChange={(e) => setForm({ ...form, duration: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-right"
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700 text-right">
              الصعوبة
            </label>
            <select
              value={form.difficulty}
              onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-right bg-white"
            >
              <option value="EASY">سهل</option>
              <option value="MEDIUM">متوسط</option>
              <option value="HARD">صعب</option>
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(`/dashboard/esp/skills/${skillId}`)}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
          >
            إلغاء
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            حفظ والانتقال للأسئلة
          </button>
        </div>
      </form>
    </div>
  );
}